// src/components/PopularTests/PopularTests.js

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useCartActions } from "../../hooks/useCartActions";
import { useNavigate } from "react-router-dom";

import { getAllTests } from "../../services/LabTestApi/PopularTest";
import { fetchAllRatings } from "../../Redux/ratingSlice";
import { 
  fetchWishlist, 
  addToWishlist, 
  removeFromWishlist,
  addWishlistOptimistic,
  removeWishlistOptimistic,
} from "../../Redux/wishlistSlice";

import {
  FaShoppingCart,
  FaStar,
  FaFlask,
  FaChevronRight,
  FaFire,
  FaHeart,
  FaRegHeart,
  FaMapMarkerAlt,
  FaChevronLeft,
} from "react-icons/fa";

import Simmer from "../Loder/Simmer";
import TestDetails from "../LabTest/TestDetails";
import LoginPopup from "../common/LoginPopup";
import Coupon from "../Coupon/Coupon";

import "../../index.css";
import { useLocationContext } from "../../context/LocationContext";

// ✅ ENV se image base URL
const API_BASE = (process.env.REACT_APP_API_URL || 'https://online-tech.in/api').replace(/\/+$/, '');
const BASE_IMG = API_BASE?.replace('/api', '');

export default function PopularTests() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { coords, selectedCity, userAddress } = useLocationContext();

  const cart = useSelector((state) => state.cart.items || []);
  
  const ratings = useSelector((state) => state.rating.ratings || {});
  const ratingLoading = useSelector((state) => state.rating.loading);
  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  const wishlistLoading = useSelector((state) => state.wishlist.loading);

  const { addWithReplaceCheck, removeItem, LoginPopupComponent } =
    useCartActions();

  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTestId, setSelectedTestId] = useState(null);
  const [imgError, setImgError] = useState({});
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  const sliderRef = useRef(null);
  const autoPlayRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // ========== WISHLIST ==========
  const getUserId = () => {
    try {
      const userData = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
      return userData?.userId || null;
    } catch {
      return null;
    }
  };

  const isInWishlist = (productId, categoryId = 2) => {
    if (!wishlistItems || !Array.isArray(wishlistItems)) return false;
    return wishlistItems.some(
      item => Number(item.productId) === Number(productId) && 
              Number(item.categoryId) === Number(categoryId)
    );
  };

  const loadWishlist = async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      await dispatch(fetchWishlist(userId)).unwrap();
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  const toggleWishlist = async (test, e) => {
    e.stopPropagation();
    const userId = getUserId();
    if (!userId) {
      setShowLoginPopup(true);
      return;
    }

    const productId = test?.testID || test?.TestID;
    const categoryId = 2;
    const isWished = isInWishlist(productId, categoryId);

    if (isWished) {
      dispatch(removeWishlistOptimistic({ productId, categoryId }));
    } else {
      dispatch(addWishlistOptimistic({ productId, categoryId }));
    }

    try {
      if (isWished) {
        await dispatch(removeFromWishlist({ userId, productId, categoryId })).unwrap();
      } else {
        await dispatch(addToWishlist({ userId, productId, categoryId })).unwrap();
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      if (isWished) {
        dispatch(addWishlistOptimistic({ productId, categoryId }));
      } else {
        dispatch(removeWishlistOptimistic({ productId, categoryId }));
      }
      alert('Failed to update wishlist. Please try again.');
    }
  };

  // ================= DISTANCE =================
  const getDistanceKm = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return 999;

    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;

    return 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * R;
  };

  const getTestRating = (testId) => {
    const key = `${testId}_2`;
    return ratings[key] || { average: 0, total: 0, ratings: [] };
  };

  // ================= CART =================
  const isInCart = (testID) =>
    cart.some(
      (i) =>
        Number(i.productId) === Number(testID) &&
        Number(i.categoryId) === 2
    );

  const buildCartItem = (test) => {
    const id = test?.testID || test?.TestID;

    const sellingPrice = Number(test?.SellingPrice) || Number(test?.sellingPrice) || 0;
    const discount = Number(test?.Discount) || Number(test?.discount) || 0;
    const originalPrice = Number(test?.testPrice) || Number(test?.TestPrice) || sellingPrice;

    return {
      productId: id,
      categoryId: 2,
      name: test?.testName || test?.TestName,
      price: sellingPrice,
      quantity: 1,
      storeId: test?.labId || test?.LabId || 0,
      originalPrice,
      discount,
    };
  };

  // ================= RATING =================
  const renderStars = (rating = 0) => {
    const fullStars = Math.floor(rating);

    return [...Array(5)].map((_, i) =>
      i < fullStars ? (
        <FaStar key={i} size={12} color="#ffc107" />
      ) : (
        <FaStar key={i} size={12} color="#ddd" />
      )
    );
  };

  // ================= GET LOCATION TEXT =================
  const getLocationText = () => {
    if (selectedCity) {
      return `Showing popular tests in ${selectedCity}`;
    }
    if (userAddress) {
      return `Showing popular tests near ${userAddress}`;
    }
    if (coords?.lat && coords?.lng) {
      return `Showing popular tests near your location`;
    }
    return "Enable location to see popular tests near you";
  };

  // ================= SLIDER FUNCTIONS =================
  const goToSlide = useCallback((index) => {
    if (!sliderRef.current) return;
    const container = sliderRef.current;
    const slides = container.children;
    if (index < 0 || index >= slides.length) return;
    
    const slideWidth = slides[0]?.offsetWidth || 200;
    const gap = 12;
    const scrollPosition = index * (slideWidth + gap);
    
    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
    
    setCurrentIndex(index);
  }, []);

  const goToNext = useCallback(() => {
    if (tests.length === 0) return;
    const nextIndex = (currentIndex + 1) % tests.length;
    goToSlide(nextIndex);
  }, [currentIndex, tests.length, goToSlide]);

  const goToPrev = useCallback(() => {
    if (tests.length === 0) return;
    const prevIndex = (currentIndex - 1 + tests.length) % tests.length;
    goToSlide(prevIndex);
  }, [currentIndex, tests.length, goToSlide]);

  // ================= AUTO PLAY =================
  useEffect(() => {
    if (isAutoPlaying && tests.length > 1) {
      autoPlayRef.current = setInterval(goToNext, 3000);
    } else {
      clearInterval(autoPlayRef.current);
    }
    return () => clearInterval(autoPlayRef.current);
  }, [isAutoPlaying, tests.length, goToNext]);

  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  // ================= TOUCH EVENTS =================
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        goToNext();
      } else {
        goToPrev();
      }
    }
  };

  // ================= UPDATE CURRENT INDEX ON SCROLL =================
  useEffect(() => {
    const container = sliderRef.current;
    if (!container) return;

    const handleScroll = () => {
      const slideWidth = container.children[0]?.offsetWidth || 200;
      const gap = 12;
      const scrollLeft = container.scrollLeft;
      const index = Math.round(scrollLeft / (slideWidth + gap));
      if (index !== currentIndex && index < tests.length) {
        setCurrentIndex(index);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [tests.length, currentIndex]);

  // ================= FETCH =================
  useEffect(() => {
    const fetchTests = async () => {
      try {
        setLoading(true);
        
        const res = await getAllTests(1, 50);

        const safeList = Array.isArray(res?.data) ? res.data : [];

        const popularTests = safeList.filter((t) => {
          return (
            t?.IsPopular === true || 
            t?.isPopular === true || 
            t?.popular === true ||
            t?.Popular === true
          );
        });

        const filtered = popularTests.filter((t) => {
          if (!coords?.lat || !coords?.lng) return false;

          const testLat = Number(t?.Latitude || t?.latitude);
          const testLng = Number(t?.Longnitude || t?.longitude || t?.longnitude);

          const hasValidCoords =
            testLat && testLng && testLat !== 0 && testLng !== 0;

          if (!hasValidCoords) return false;

          const distance = getDistanceKm(
            coords.lat,
            coords.lng,
            testLat,
            testLng
          );

          if (selectedCity) {
            return distance <= 100;
          }

          return distance <= 20;
        });

        setTests(filtered);

        if (filtered.length > 0) {
          const ratingProducts = filtered.map((test) => {
            const id = test?.testID || test?.TestID;
            return {
              productId: id,
              categoryId: 2
            };
          });
          
          await dispatch(fetchAllRatings(ratingProducts));
        }
        
        await loadWishlist();
        
      } catch (err) {
        console.log(err);
        setTests([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTests();
  }, [coords, selectedCity, dispatch]);

  // ✅ Handle View All Tests click
  const handleViewAllTests = () => {
    navigate("/all-tests");
  };

  // ================= UI =================
  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 bg-white rounded-xl shadow-lg mt-3">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-4">
            <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse"></span>
            <span className="text-xs font-bold tracking-wide">⏳ LOADING TESTS</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mt-4">
            {[...Array(6)].map((_, i) => (
              <Simmer key={i} height="220px" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!coords?.lat || !coords?.lng) {
    return (
      <div className="px-4 md:px-8 py-6 bg-white rounded-xl shadow-lg mt-3 text-center">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-4 py-1.5 rounded-full shadow-md mb-4">
          <FaMapMarkerAlt size={12} className="text-white" />
          <span className="text-xs font-bold tracking-wide">LOCATION REQUIRED</span>
        </div>
        <div className="flex flex-col items-center justify-center py-8">
          <FaMapMarkerAlt size={48} className="text-gray-300 mb-4" />
          <p className="text-gray-600 font-medium">Please enable location access</p>
          <p className="text-gray-400 text-sm mt-1">Allow location to see popular tests near you</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-6 py-2 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (tests.length === 0) {
    return (
      <div className="px-4 md:px-8 py-6 bg-white rounded-xl shadow-lg mt-3 text-center">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-4">
          <FaFire size={12} className="text-yellow-400" />
          <span className="text-xs font-bold tracking-wide">POPULAR TESTS</span>
        </div>
        <div className="flex flex-col items-center justify-center py-8">
          <FaFlask size={48} className="text-gray-300 mb-4" />
          <p className="text-gray-600 font-medium">No popular tests found near you</p>
          <p className="text-gray-400 text-sm mt-1 max-w-md">
            {selectedCity 
              ? `No popular tests available in ${selectedCity} within 100km radius`
              : "No popular tests available within 20km radius"}
          </p>
          <button
            onClick={handleViewAllTests}
            className="mt-4 px-6 py-2 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600 transition-colors"
          >
            View All Tests
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">

      {/* Header Section */}
      <div className="text-center mb-8">
        
        
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">
          <span className="bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent">
            Popular
          </span>{" "}
          <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
            Health Tests
          </span>
        </h2>
        
        <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
          {getLocationText()}
        </p>
        
        <p className="text-xs text-gray-400 mt-1">
          {tests.length} popular tests available
        </p>
        
        {/* ✅ See All Button */}
        <button
          onClick={handleViewAllTests}
          className="group flex items-center gap-2 bg-white border-2 border-red-500 text-red-600 hover:bg-red-500 hover:text-white transition-all duration-300 px-5 py-2 rounded-full font-semibold text-sm shadow-md mx-auto mt-4"
        >
          <span>View All Tests</span>
          <FaChevronRight size={12} className="group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      {/* ✅ Slider */}
      <div 
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Navigation Arrows */}
        {tests.length > 1 && (
          <>
            <button
              onClick={goToPrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-gray-700 p-2 rounded-full shadow-lg border border-gray-200 transition-all duration-200 hover:scale-110 -ml-3"
              aria-label="Previous slide"
            >
              <FaChevronLeft size={18} />
            </button>
            
            <button
              onClick={goToNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-gray-700 p-2 rounded-full shadow-lg border border-gray-200 transition-all duration-200 hover:scale-110 -mr-3"
              aria-label="Next slide"
            >
              <FaChevronRight size={18} />
            </button>
          </>
        )}

        {/* Slider Container */}
        <div 
          ref={sliderRef}
          className="flex gap-3 overflow-x-auto scroll-smooth hide-scrollbar"
          style={{ scrollSnapType: 'x mandatory' }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {tests.map((test) => {
            const id = test?.testID || test?.TestID;
            const inCart = isInCart(id);
            const inWishlist = isInWishlist(id, 2);

            const sellingPrice = Number(test?.SellingPrice) || Number(test?.sellingPrice) || 0;
            const discount = Number(test?.Discount) || Number(test?.discount) || 0;
            const originalPrice = Number(test?.testPrice) || Number(test?.TestPrice) || sellingPrice;
                
            const hasDiscount = discount > 0 && sellingPrice < originalPrice;
            const discountPercent = originalPrice > 0 && discount > 0
              ? Math.round((discount / originalPrice) * 100)
              : 0;

            const isPriceAvailable = sellingPrice > 0 || originalPrice > 0;

            const { average: avgRating, total: totalReviews } = getTestRating(id);
            const isPopular = test?.IsPopular === true;

            const reportTime = test?.ReportTime;
            const reportTimeType = test?.ReportTimeType;
            let reportText = "";
            if (reportTime) {
              const unit = reportTimeType === 2 ? "days" : "hours";
              reportText = `⏱️ Reports in ${reportTime} ${unit}`;
            }

            // ✅ Test Image URL
            let testImageUrl = "";
            if (test?.TestImage) {
              testImageUrl = `${BASE_IMG}/TestMasterImage/${test.TestImage}`;
            }

            // ✅ Lab Image URL
            let labImageUrl = "";
            const labImage = test?.LabImage || test?.labImage;
            if (labImage) {
              labImageUrl = `${BASE_IMG}/LabMasterImage/${labImage}`;
            }

            const errorState = imgError[id];
            let imageSrc = null;
            let showFallbackIcon = false;

            if (errorState === 'lab') {
              showFallbackIcon = true;
            } else if (errorState === 'test') {
              if (labImageUrl) {
                imageSrc = labImageUrl;
              } else {
                showFallbackIcon = true;
              }
            } else {
              if (testImageUrl) {
                imageSrc = testImageUrl;
              } else {
                if (labImageUrl) {
                  imageSrc = labImageUrl;
                } else {
                  showFallbackIcon = true;
                }
              }
            }

            const labId = test?.labId || test?.LabId;

            return (
              <div
                key={id}
                className="min-w-[200px] max-w-[200px] flex-shrink-0 snap-start"
              >
                {/* ✅ Card UI */}
                <div className="bg-white border rounded-xl p-3 h-[380px] flex flex-col hover:shadow-lg transition-all duration-300 hover:-translate-y-1 relative">
                  
                  {/* ✅ Wishlist Button */}
                  <button
                    onClick={(e) => toggleWishlist(test, e)}
                    className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-white/80 backdrop-blur-sm shadow-md hover:bg-white transition-all duration-200"
                    disabled={wishlistLoading}
                  >
                    {wishlistLoading ? (
                      <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      inWishlist ? (
                        <FaHeart size={14} className="text-red-500 fill-red-500" />
                      ) : (
                        <FaRegHeart size={14} className="text-gray-400 hover:text-red-500" />
                      )
                    )}
                  </button>

                  {/* ✅ YELLOW POPULAR BADGE */}
                  {isPopular && (
                    <div className="absolute top-2 left-2 z-10">
                      <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-lg flex items-center gap-1">
                        <FaStar size={9} className="fill-white" />
                        POPULAR
                      </div>
                    </div>
                  )}

                  {/* ✅ DISCOUNT BADGE */}
                  {hasDiscount && discount > 0 && isPriceAvailable && (
                    <div className="absolute top-8 left-2 z-10">
                      <div className="bg-gradient-to-r from-red-500 to-red-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-md">
                        {discountPercent}% OFF
                      </div>
                    </div>
                  )}

                  {/* ✅ Image Section */}
                  <div
                    className="relative w-full h-[140px] cursor-pointer rounded-xl overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100"
                    onClick={() => setSelectedTestId(id)}
                  >
                    {!showFallbackIcon && imageSrc ? (
                      <img
                        src={imageSrc}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        alt={test?.testName || test?.TestName}
                        onError={() => {
                          if (errorState === undefined && testImageUrl) {
                            setImgError(prev => ({ ...prev, [id]: 'test' }));
                          } else if (errorState === 'test' && labImageUrl) {
                            setImgError(prev => ({ ...prev, [id]: 'lab' }));
                          } else {
                            setImgError(prev => ({ ...prev, [id]: 'lab' }));
                          }
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FaFlask size={48} className="text-gray-300" />
                      </div>
                    )}
                    
                    <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>

                  {/* ✅ Test Name */}
                  <div className="text-[14px] font-bold text-center line-clamp-2 mt-2.5 px-1 min-h-[40px] text-gray-800">
                    {test?.testName || test?.TestName}
                  </div>

                  {/* ✅ Ratings */}
                  <div className="flex justify-center items-center gap-1 mt-1">
                    <div className="flex gap-0.5">
                      {renderStars(avgRating || 0)}
                    </div>
                    {!ratingLoading && totalReviews > 0 && (
                      <span className="text-[10px] text-gray-400 font-medium">
                        ({totalReviews} reviews)
                      </span>
                    )}
                  </div>

                  {/* ✅ Lab Name */}
                  {test?.labName && (
                    <div className="text-[10px] text-gray-400 text-center truncate px-1 mt-1">
                      {test.labName}
                    </div>
                  )}

                  {/* ✅ Report Time Badge */}
                  {reportText && (
                    <div className="flex justify-center items-center mt-1.5">
                      <span className="text-[10px] font-medium text-red-600 bg-red-50 px-2.5 py-1 rounded-full border border-red-200">
                        {reportText}
                      </span>
                    </div>
                  )}

                  {/* 🔥 Price Section OR Coupon */}
                  <div className="mt-auto pt-2.5 border-t border-gray-100">
                    {hasDiscount && discount > 0 && isPriceAvailable ? (
                      // ---------- DISCOUNT SECTION ----------
                      <div className="flex justify-between items-center">
                        <div className="flex flex-col">
                          <div className="text-lg font-extrabold text-green-600">
                            ₹{sellingPrice}
                          </div>
                          {originalPrice > 0 && (
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs line-through text-gray-400">
                                ₹{originalPrice}
                              </span>
                              <span className="text-[11px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-md">
                                Save ₹{discount}
                              </span>
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() =>
                            inCart
                              ? removeItem(buildCartItem(test))
                              : addWithReplaceCheck(buildCartItem(test))
                          }
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-semibold border transition-all duration-200
                            ${inCart
                              ? "bg-red-50 text-red-600 border-red-500 hover:bg-red-100"
                              : "bg-blue-100 text-blue-600 border-blue-500 hover:bg-blue-200"
                            }`}
                        >
                          <FaShoppingCart size={10} />
                          {inCart ? "Remove" : "Add"}
                        </button>
                      </div>
                    ) : (
                      // ---------- COUPON SECTION ----------
                      <div className="flex flex-col gap-1">
                        {/* Price and Add to Cart in same row */}
                        <div className="flex justify-between items-center">
                          {/* Price */}
                          {isPriceAvailable && (
                            <div className="flex flex-col">
                              <div className="text-sm font-bold text-green-600">
                                ₹{sellingPrice || originalPrice}
                              </div>
                              {originalPrice > 0 && sellingPrice > 0 && originalPrice !== sellingPrice && (
                                <span className="text-xs line-through text-gray-400">
                                  ₹{originalPrice}
                                </span>
                              )}
                              {!hasDiscount && originalPrice > 0 && sellingPrice > 0 && originalPrice === sellingPrice && (
                                <span className="text-[9px] text-gray-400">No Discount</span>
                              )}
                            </div>
                          )}

                          {/* Add to Cart Button */}
                          <button
                            onClick={() =>
                              inCart
                                ? removeItem(buildCartItem(test))
                                : addWithReplaceCheck(buildCartItem(test))
                            }
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-semibold border transition-all duration-200
                              ${inCart
                                ? "bg-red-50 text-red-600 border-red-500 hover:bg-red-100"
                                : "bg-blue-100 text-blue-600 border-blue-500 hover:bg-blue-200"
                              }`}
                          >
                            <FaShoppingCart size={10} />
                            {inCart ? "Remove" : "Add"}
                          </button>
                        </div>

                        {/* Coupon Component - Below Price & Add to Cart */}
                        <Coupon
                          labId={labId}
                          userId={getUserId()}
                          testPrice={originalPrice}
                          sellingPrice={sellingPrice}
                          discount={discount}
                          showCopyButton={false}
                          showAddToCart={false}
                          isInCart={inCart}
                          onAddToCart={() => addWithReplaceCheck(buildCartItem(test))}
                          onRemoveFromCart={() => removeItem(buildCartItem(test))}
                          className="w-full"
                          loadingText="Loading coupon..."
                          noCouponText="No coupon available"
                          labelText="🎫 No Discount - Coupon"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ❌ Dots Indicator - REMOVED */}
      </div>

      {/* Trust Badges */}
      {tests.length > 0 && (
        <div className="mt-8 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-[10px] font-bold">✓</span>
              </div>
              <span>Free Home Pickup</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-[10px] font-bold">⚡</span>
              </div>
              <span>Reports in 4 Hours</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 text-[10px] font-bold">🏆</span>
              </div>
              <span>NABL Certified</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-[10px] font-bold">❤️</span>
              </div>
              <span>10L+ Customers</span>
            </div>
          </div>
        </div>
      )}

      {selectedTestId && (
        <TestDetails
          id={selectedTestId}
          onClose={() => setSelectedTestId(null)}
        />
      )}
      
      <LoginPopup
        isOpen={showLoginPopup}
        onClose={() => setShowLoginPopup(false)}
      />
      
      {LoginPopupComponent}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
} 